import {
  PostApplicationRequestBody,
  PutApplicationRequestBody,
  fetchPostApplication,
  fetchPostState,
  fetchPostThreatmetrixEvent,
  fetchPutApplication,
  fetchPutState,
  fetchPutThreatmetrixEvent,
} from '@iwoca/lapi-client/edge';
import { API_AUTH_TOKEN } from '@iwoca/lapi-client/lendingAPI';
import { useMutation } from '@tanstack/react-query';

import { TPutStateRequestBodyDE } from '../../lapiOverrideTypesDE';
import { IwQueryClient } from '../../store/IwQueryClient';
import { TPostStateRequestBody, TPutStateRequestBody } from '../../types';

export function usePostApplicationMutation() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({
      body,
      stateKey,
    }: {
      body: PostApplicationRequestBody;
      stateKey: string;
    }) => {
      await fetchPostApplication({
        body,
        stateKey,
      });
    },
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetApplication'],
      });
    },
  });

  return {
    mutatePostApplication: mutateAsync,
  };
}

export function usePutApplicationMutation() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({
      body,
      stateKey,
    }: {
      body: PutApplicationRequestBody;
      stateKey: string;
    }) => {
      await fetchPutApplication({
        body,
        stateKey,
      });
    },
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetApplication'],
      });
    },
  });

  return {
    mutatePutApplication: mutateAsync,
  };
}

export function usePostStateMutation() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({ body }: { body: TPostStateRequestBody }) => {
      const stateResponse = await fetchPostState({
        headers: {
          authorization: API_AUTH_TOKEN,
        },
        body,
      });

      return stateResponse.data?.state_key;
    },
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetProfile'],
      });
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetContext'],
      });
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetState'],
      });
    },
  });

  return {
    mutatePostState: mutateAsync,
  };
}

export async function fetchPutAndGetState({
  body,
  stateKey,
}: {
  body: TPutStateRequestBody | TPutStateRequestBodyDE;
  stateKey: string;
}) {
  await fetchPutState({
    //@ts-expect-error LAPI doesn't handle DE typing
    body,
    stateKey,
  });

  await IwQueryClient.getOrCreate().invalidateQueries({
    queryKey: ['fetchGetState'],
  });
}

export function usePostThreatMetrixMutation() {
  const { data, mutateAsync } = useMutation({
    mutationFn: async ({ stateKey }: { stateKey: string }) => {
      const response = await fetchPostThreatmetrixEvent({
        body: { data: { page_id: 1 } },
        stateKey,
      });

      return {
        tmSessionId: response.data.tm_session_id,
      };
    },
  });

  return {
    tmSessionId: data?.tmSessionId,
    mutatePostThreatMetrix: mutateAsync,
  };
}

export function usePutThreatMetrixMutation() {
  const { mutate } = useMutation({
    mutationFn: async ({
      tmSessionId,
      stateKey,
    }: {
      tmSessionId: string;
      stateKey: string;
    }) => {
      await fetchPutThreatmetrixEvent({
        body: { data: { is_profiled: true } },
        stateKey,
        tmSessionId,
      });
    },
  });

  return {
    mutatePutThreatMetrix: mutate,
  };
}
